import { Serializer, SerializerExtensions } from "@megaron/serializers";
import { uriMatcher } from "@megaron/uri";

import { budgetFiltersSerializer } from "./budgetFilters";
import { cartFiltersSerializer } from "./cartFilters";
import { blikCheckFiltersSerializer, cashRewardsAccountFiltersSerializer } from "./cashRewardsFilters";
import { customerAnalyticsViewFiltersSerializer } from "./customerAnalyticsFilters";
import { customerFiltersSerializer } from "./customerFilters";
import { fulfillmentOrderFiltersSerializer } from "./fulfillmentOrderFilters";
import { invoiceThreadFiltersSerializer } from "./invoiceThreadFilters";
import { itemFiltersSerializer } from "./itemFilters";
import { notificationFiltersSerializer } from "./notificationFilters";
import { orderFiltersSerializer } from "./orderFilters";
import { priceListsFiltersSerializer } from "./priceListFilters";
import { productAvailabilityFiltersSerializer } from "./productAvailabilityFilters";
import { regionProfitMetricsFiltersSerializer } from "./regionProfitMetricsFilters";
import { supportTicketFiltersSerializer } from "./supportTicketFilters";
import { megaraxUriSchema } from "./uri";

const withFilters = <T>(
  path: string,
  serializer: Serializer<T> & SerializerExtensions<T>,
  filters: T | undefined,
): string => {
  if (filters === undefined) return path;

  const filtersStr = serializer.asJsonString().serialize(filters);

  return `${path}?filters=${encodeURIComponent(filtersStr)}`;
};

export const getMegaraxPath = uriMatcher(megaraxUriSchema)<string>({
  "megarax://home": () => "/",
  "megarax://history": () => "/history",
  "megarax://signals": () => "/signals",

  "megarax://crm/home": () => "/crm/home",
  "megarax://crm/my-feed": () => "/crm/my-feed",
  "megarax://crm/customers": ({ filters }) => withFilters("/crm/customers", customerFiltersSerializer, filters),
  "megarax://crm/customers/new": () => "/crm/customers/new",
  "megarax://crm/customers/new-list": ({ filters }) =>
    withFilters("/crm/customers/new-list", customerFiltersSerializer, filters),
  "megarax://crm/customers/new-map": ({ filters }) =>
    withFilters("/crm/customers/new-map", customerFiltersSerializer, filters),
  "megarax://crm/customers/new-customer-analytics": ({ filters }) =>
    withFilters("/crm/customers/new-customer-analytics", customerAnalyticsViewFiltersSerializer, filters),
  "megarax://crm/customer-lists/id/{id}": ({ id, filters }) =>
    withFilters(`/crm/customer-lists/id/${id}`, customerFiltersSerializer, filters),
  "megarax://crm/customers/id/{id}": ({ id }) => `/crm/customers/id/${id}`,

  "megarax://crm/orders": ({ filters }) => withFilters("/crm/orders", orderFiltersSerializer, filters),
  "megarax://crm/orders/number/{number}": ({ number }) => `/crm/orders/number/${number}`,

  "megarax://crm/order-drafts": () => "/crm/order-drafts",
  "megarax://crm/order-drafts/new": () => "/crm/order-drafts/new",
  "megarax://crm/order-drafts/id/{id}": ({ id }) => `/crm/order-drafts/id/${id}`,

  "megarax://crm/my-visits": () => "/crm/my-visits",
  "megarax://crm/salespeople/id/{id}": ({ id }) => `/crm/salespeople/id/${id}`,
  "megarax://crm/visits/id/{id}": ({ id }) => `/crm/visits/id/${id}`,
  "megarax://crm/salesperson-visits/id/{id}": ({ id }) => `/crm/salesperson-visits/id/${id}`,
  "megarax://crm/salesperson-visits/id/{id}/{view}": ({ id, view }) => `/crm/salesperson-visits/id/${id}/${view}`,
  "megarax://crm/salesperson-visits/id/{id}/{view}/{date}": ({ id, view, date }) =>
    `/crm/salesperson-visits/id/${id}/${view}/${date}`,
  "megarax://crm/salesperson-visits/id/{id}/{view}/{date}/visits/id/{visitId}": ({ id, view, date, visitId }) =>
    `/crm/salesperson-visits/id/${id}/${view}/${date}/visits/id/${visitId}`,

  "megarax://crm/regions": () => "/crm/regions",
  "megarax://crm/regions/id/{id}": ({ id, filters }) =>
    withFilters(`/crm/regions/id/${id}`, regionProfitMetricsFiltersSerializer, filters),

  "megarax://crm/trade-routes": () => "/crm/trade-routes",
  "megarax://crm/trade-routes/id/{id}": ({ id }) => `/crm/trade-routes/id/${id}`,

  "megarax://crm/price-reports": () => "/crm/price-reports",
  "megarax://crm/price-report-products": () => "/crm/price-report-products",

  "megarax://crm/diff-sync/{channel}": ({ channel }) => `/crm/diff-sync/${channel}`,

  "megarax://crm/post-sale-discounts": () => "/crm/post-sale-discounts",
  "megarax://crm/profit-targets": () => "/crm/profit-targets",
  "megarax://crm/my-affiliate-codes": () => "/crm/my-affiliate-codes",
  "megarax://crm/my-affiliate-codes/id/{id}": ({ id }) => `/crm/my-affiliate-codes/id/${id}`,
  "megarax://crm/product-availability": ({ filters }) =>
    withFilters("/crm/product-availability", productAvailabilityFiltersSerializer, filters),
  "megarax://crm/training-calendar": () => "/crm/training-calendar",
  "megarax://crm/reports": () => "/crm/reports",
  "megarax://crm/reports/id/{id}": ({ id }) => `/crm/reports/id/${id}`,

  "megarax://catalog/items": ({ filters }) => withFilters("/catalog/items", itemFiltersSerializer, filters),
  "megarax://catalog/items/id/{id}": ({ id }) => `/catalog/items/id/${id}`,
  "megarax://catalog/price-lists": ({ filters }) =>
    withFilters("/catalog/price-lists", priceListsFiltersSerializer, filters),
  "megarax://catalog/unit-templates": () => "/catalog/unit-templates",
  "megarax://catalog/unit-templates/debug": () => "/catalog/unit-templates/debug",

  "megarax://catalog/diff-sync/{channel}": ({ channel }) => `/catalog/diff-sync/${channel}`,

  "megarax://invoices/threads/id/{id}": ({ id }) => `/invoices/threads/id/${id}`,
  "megarax://invoices/threads": ({ filters }) =>
    withFilters("/invoices/threads", invoiceThreadFiltersSerializer, filters),
  "megarax://invoices/budgets/id/{id}": ({ id }) => `/invoices/budgets/id/${id}`,
  "megarax://invoices/budgets": ({ filters }) => withFilters("/invoices/budgets", budgetFiltersSerializer, filters),
  "megarax://invoices/budgets/new": () => "/invoices/budgets/new",
  "megarax://invoices/drafts/id/{id}": ({ id }) => `/invoices/drafts/id/${id}`,
  "megarax://invoices/drafts": () => "/invoices/drafts",
  "megarax://invoices/drafts/new": () => "/invoices/drafts/new",

  "megarax://iam/users/email/{email}": ({ email }) => `/iam/users/email/${email}`,
  "megarax://iam/users": () => "/iam/users",
  "megarax://iam/groups/id/{id}": ({ id }) => `/iam/groups/id/${id}`,
  "megarax://iam/groups": () => "/iam/groups",
  "megarax://iam/groups/new": () => "/iam/groups/new",

  "megarax://support/tickets/id/{id}": ({ id }) => `/support/tickets/id/${id}`,
  "megarax://support/tickets": ({ filters }) =>
    withFilters("/support/tickets", supportTicketFiltersSerializer, filters),
  "megarax://support/tickets/new": () => "/support/tickets/new",
  "megarax://support/settings": () => "/support/settings",

  "megarax://fulfillment/orders": ({ filters }) =>
    withFilters("/fulfillment/orders", fulfillmentOrderFiltersSerializer, filters),
  "megarax://fulfillment/orders/id/{id}": ({ id }) => `/fulfillment/orders/id/${id}`,

  "megarax://loyalty/lines": () => "/loyalty/lines",
  "megarax://loyalty/lines/id/{id}": ({ id }) => `/loyalty/lines/id/${id}`,
  "megarax://loyalty/batches": () => "/loyalty/batches",
  "megarax://loyalty/ungroup-code": () => "/loyalty/ungroup-code",
  "megarax://loyalty/production-batches/id/{id}": ({ id }) => `/loyalty/production-batches/id/${id}`,
  "megarax://loyalty/label-batches/id/{id}": ({ id }) => `/loyalty/label-batches/id/${id}`,
  "megarax://loyalty/code-generation-orders": () => "/loyalty/code-generation-orders",
  "megarax://loyalty/code-groups": () => "/loyalty/code-groups",
  "megarax://loyalty/inspect-code": () => "/loyalty/inspect-code",
  "megarax://loyalty/inspect-code/id/{id}": ({ id }) => `/loyalty/inspect-code/id/${id}`,
  "megarax://loyalty/blik-checks": ({ filters }) =>
    withFilters("/loyalty/blik-checks", blikCheckFiltersSerializer, filters),
  "megarax://loyalty/accounts": ({ filters }) =>
    withFilters("/loyalty/accounts", cashRewardsAccountFiltersSerializer, filters),
  "megarax://loyalty/accounts/id/{id}": ({ id }) => `/loyalty/accounts/id/${id}`,
  "megarax://loyalty/carts": ({ filters }) => withFilters("/loyalty/carts", cartFiltersSerializer, filters),
  "megarax://loyalty/carts/id/{id}": ({ id }) => `/loyalty/carts/id/${id}`,
  "megarax://loyalty/listings": () => "/loyalty/listings",

  "megarax://dev/environment": () => "/dev/environment",
  "megarax://dev/notifications": ({ filters }) =>
    withFilters("/dev/notifications", notificationFiltersSerializer, filters),
  "megarax://dev/notifications/id/{id}": ({ id }) => `/dev/notifications/id/${id}`,
  "megarax://dev/docs": () => "/dev/docs",
});
